import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type { ApiPostResponse, ApiSuccess } from "@AxiosWrapper";

type TransmittalDetailsState = ApiSuccess<"/transmittals/getTransmittalDataById">["data"] | null;
type TransmittalMetadataState = ApiSuccess<"/transmittals/getTransmittalDataById">["metadata"] | null;
type TransmittalAccessState = ApiSuccess<"/transmittals/isTransmittal403">;
type TransmittalVersionsState = ApiPostResponse<"/transmittals/getTransmittalFileVersionsForSpace">;
/**
 * `mergeTransmitalsData` is dispatched with values that may be absent, and a case reducer that
 * returns `undefined` is "no replacement" to Immer, so the slice is left untouched.
 */
type TransmittalDetailsPayload = TransmittalDetailsState | undefined;
/**
 * Nothing dispatches or selects this slice today, so its element shape is unproven; `unknown[]`
 * keeps it honest and forces whoever wires it up to narrow. `never[]` — what `initialState: []`
 * inferred — would have made every future dispatch impossible.
 */
type TransmittalReferencesState = unknown[];

const saveTransmitalSlice = createSlice({
  name: "transmittals",
  initialState: null as TransmittalDetailsState,
  reducers: {
    setTransmitalsData: (_state, action: PayloadAction<TransmittalDetailsPayload>) => action.payload,
    mergeTransmitalsData: (state, action: PayloadAction<TransmittalDetailsPayload>) => {
      const next = action.payload;
      if (next === undefined) return state;
      if (next === null) return null;
      return state === null ? next : { ...state, ...next };
    },
  },
});

const transmittalMetadataSlice = createSlice({
  name: "transmittalMetadata",
  initialState: null as TransmittalMetadataState,
  reducers: {
    setTransmitalMetadata: (_state, action: PayloadAction<TransmittalMetadataState>) => action.payload,
  },
});

/**
 * `null` = the verdict has NOT come back yet, which is a different thing from "denied" and must stay
 * representable: seeding a denied verdict instead paints the detail page's 403 screen on every cold
 * load until `/isTransmittal403` answers. `clearTransmittalAccess` returns it to `null` when the
 * transmittal changes, so one transmittal's verdict never stands in for the next one's.
 */
const transmittalAccessSlice = createSlice({
  name: "transmittalAccess",
  // `as` and not a typed `const`: a `const` annotated `TransmittalAccessState | null` and assigned
  // `null` narrows to `null` at the use site, and createSlice would infer that as the state type.
  initialState: null as TransmittalAccessState | null,
  reducers: {
    setTransmittalAccess: (_state, action: PayloadAction<TransmittalAccessState>) => action.payload,
    /** Back to "unresolved" — dispatched when the transmittal changes, before the check re-runs. */
    clearTransmittalAccess: () => null,
  },
});

const transmittalDataForReviewsSlice = createSlice({
  name: "transmittalDataForReviews",
  // `boolean | null`, not the `null` that a bare `initialState: null` inferred: CreateTransmittal
  // dispatches `setTransmittalDataForReviews(true)` and FilesInReview dispatches `(null)`, so the
  // store really does hold a boolean. FilesInReview widened its own selector to `boolean | null`
  // and left a note that the fix belonged here — this is that fix.
  initialState: null as boolean | null,
  reducers: {
    setTransmittalDataForReviews: (_state, action: PayloadAction<boolean | null>) => action.payload,
  },
});

const transmittalReferencesSlice = createSlice({
  name: "transmittalReferences",
  initialState: [] as TransmittalReferencesState,
  reducers: {
    setTransmittalReferences: (_state, action: PayloadAction<TransmittalReferencesState>) => action.payload,
  },
});

const transmittalVersionsSlice = createSlice({
  name: "transmittalVersions",
  initialState: [] as TransmittalVersionsState,
  reducers: {
    setTransmittalVersions: (_state, action: PayloadAction<TransmittalVersionsState>) => action.payload,
  },
});

// Every slice above now declares its payload, so these action creators carry a real argument type.
// `setTransmittalAccess` alone is re-wrapped below, to add excess-property checking through a
// variable — which `Exact<>` can express and a plain `PayloadAction` cannot.
export const { setTransmitalsData, mergeTransmitalsData } = saveTransmitalSlice.actions;
export const { setTransmitalMetadata } = transmittalMetadataSlice.actions;
export const { setTransmittalDataForReviews } = transmittalDataForReviewsSlice.actions;
export const { setTransmittalReferences } = transmittalReferencesSlice.actions;
export const { setTransmittalVersions } = transmittalVersionsSlice.actions;

const { setTransmittalAccess: _setTransmittalAccessRaw, clearTransmittalAccess } = transmittalAccessSlice.actions;
export { clearTransmittalAccess };

/**
 * Object-payload action creator wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 */
export const setTransmittalAccess = <T extends Exact<TransmittalAccessState, T>>(payload: T) =>
  _setTransmittalAccessRaw(payload);

export const saveTransmital = saveTransmitalSlice.reducer;
export const saveTransmitalMetadata = transmittalMetadataSlice.reducer;
export const setTransmittalAccessReducer = transmittalAccessSlice.reducer;
export const setTransmittalDataForReviewsReducer = transmittalDataForReviewsSlice.reducer;
export const setTransmittalReferencesReducer = transmittalReferencesSlice.reducer;
export const setTransmittalVersionsReducer = transmittalVersionsSlice.reducer;
